import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '@components/ui/feedback';
import { useAddOrgBundleMutation } from '@features/organizations';
import type { BillingInterval } from '@features/subscriptions';
import {
  BillingIntervalToggle,
  slugifyBundleName,
  useBundleCatalog,
  useGetFullSubscriptionDetailsQuery,
} from '@features/subscriptions';
import { useOrganizationSize } from '@features/user';
import { CheckCircle, Security } from '@mui/icons-material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Card, CardContent, Link, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { BAAFormData } from './components/BAASetupModal';
import type { COIFormData } from './components/COISetupModal';
import type { DataRetentionFormData } from './components/DataRetentionSetupModal';
import type { ServiceStatus } from './components/DataSecurityServiceCard';
import {
  useGetDataSecurityStatusQuery,
  useSubmitBAAMutation,
  useSubmitCOIMutation,
  useSubmitDataRetentionMutation,
} from './store/api';
import {
  BAASetupModal,
  COISetupModal,
  DataRetentionSetupModal,
  DataSecurityPurchaseDialog,
  DataSecurityServiceCard,
  SecurityOverviewSection,
} from './components';

// Helper to detect internal URLs (start with /)
const isInternalUrl = (url: string) => url.startsWith('/');

/**
 * Maps API status string to UI ServiceStatus type
 * API returns: 'not_entitled' | 'not_configured' | 'processing' | 'active'
 * UI expects: 'not_configured' | 'processing' | 'active'
 */
function mapApiStatusToServiceStatus(apiStatus: string | undefined): ServiceStatus {
  if (!apiStatus || apiStatus === 'not_entitled' || apiStatus === 'not_configured') {
    return 'not_configured';
  }
  if (apiStatus === 'processing') {
    return 'processing';
  }
  if (apiStatus === 'active') {
    return 'active';
  }
  return 'not_configured';
}

export default function DataSecurityPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  // Modal visibility state
  const [showBAAModal, setShowBAAModal] = useState(false);
  const [showCOIModal, setShowCOIModal] = useState(false);
  const [showRetentionModal, setShowRetentionModal] = useState(false);

  const organizationSize = useOrganizationSize();
  const { dataSecurity, annualDiscountRange, isLoading: isLoadingCatalog } = useBundleCatalog();

  // Data security status query and mutations
  const { data: dataSecurityStatus, isLoading: isLoadingDataSecurityStatus } =
    useGetDataSecurityStatusQuery();
  const [submitBAA] = useSubmitBAAMutation();
  const [submitCOI] = useSubmitCOIMutation();
  const [submitDataRetention] = useSubmitDataRetentionMutation();

  const [addOrgBundle, { isLoading: isAddingBundle }] = useAddOrgBundleMutation();
  const { dataSecurity: dataSecurityTracking, error: errorTracking } = useEventTracking();

  // Map API statuses to UI ServiceStatus
  const serviceStatuses = useMemo(
    () => ({
      baa: mapApiStatusToServiceStatus(dataSecurityStatus?.baa?.status),
      coi: mapApiStatusToServiceStatus(dataSecurityStatus?.coi?.status),
      dataRetention: mapApiStatusToServiceStatus(dataSecurityStatus?.data_retention?.status),
    }),
    [dataSecurityStatus],
  );

  // Check if user already has data_security bundle
  const hasDataSecurityBundle = dataSecurityStatus?.has_bundle ?? false;

  // Get the data_security bundle from catalog
  const dataSecurityBundle = useMemo(() => {
    return dataSecurity.length > 0 ? dataSecurity[0] : null;
  }, [dataSecurity]);

  // Default to /bundles with tab derived from bundle name if no learn_more_url is provided
  const learnMoreUrl =
    dataSecurityBundle?.learn_more_url ||
    `/bundles?tab=${dataSecurityBundle?.name ? slugifyBundleName(dataSecurityBundle.name) : 'security-data'}`;

  // Default the billing-interval toggle to the org's actual cycle. The
  // "Add to plan" mutation doesn't carry billing_interval — the BE attaches
  // the bundle at whatever the org is already on — so a hardcoded 'year'
  // default was misleading on Monthly orgs (toggle said annual, BE charged
  // monthly). Toggle stays interactive as a price-preview affordance.
  const { data: subscriptionDetails } = useGetFullSubscriptionDetailsQuery();
  const orgBillingInterval: BillingInterval =
    subscriptionDetails?.billing_interval === 'year' ? 'year' : 'month';
  const [billingInterval, setBillingInterval] = useState<BillingInterval | null>(null);
  const effectiveBillingInterval: BillingInterval = billingInterval ?? orgBillingInterval;
  const isPreviewingOtherInterval =
    billingInterval !== null && billingInterval !== orgBillingInterval;

  const isLoading = isLoadingCatalog || isLoadingDataSecurityStatus;

  const handleAddToPlan = () => {
    setPurchaseError(null);
    setShowPurchaseDialog(true);
    dataSecurityTracking.addToPlanClicked({
      bundle_id: dataSecurityBundle?.bundle_id,
      bundle_name: dataSecurityBundle?.name,
      billing_interval: effectiveBillingInterval,
      organization_size: organizationSize,
    });
  };

  const handleLearnMoreClick = (e: React.MouseEvent) => {
    // For internal URLs, use React Router navigation with state
    if (isInternalUrl(learnMoreUrl)) {
      e.preventDefault();
      navigate(learnMoreUrl, {
        state: {
          showAddToPlan: true,
          returnPath: location.pathname + location.search,
        },
      });
    }
    // External URLs will follow the href naturally
  };

  const handleConfirmPurchase = async () => {
    if (!dataSecurityBundle) {
      setPurchaseError('Unable to add bundle. Please try again.');
      return;
    }

    setPurchaseError(null);

    const trackingMeta = {
      bundle_id: dataSecurityBundle.bundle_id,
      bundle_name: dataSecurityBundle.name,
      billing_interval: effectiveBillingInterval,
      organization_size: organizationSize,
    };

    dataSecurityTracking.purchaseConfirmed(trackingMeta);

    try {
      await addOrgBundle({
        adminAddBundleRequest: {
          bundle_id: dataSecurityBundle.bundle_id,
        },
      }).unwrap();

      dataSecurityTracking.purchaseSucceeded(trackingMeta);

      // Close dialog on success
      // Cache invalidation will automatically trigger a refetch of data security status
      setShowPurchaseDialog(false);
    } catch (err: any) {
      const errorMessage =
        err?.data?.message || err?.message || 'Failed to add bundle. Please try again.';
      setPurchaseError(errorMessage);
      dataSecurityTracking.purchaseFailed({
        bundle_id: dataSecurityBundle.bundle_id,
        bundle_name: dataSecurityBundle.name,
        error_message: errorMessage,
      });
    }
  };

  // Always show monthly equivalent (divide yearly by 12 when annual)
  const price =
    dataSecurityBundle && effectiveBillingInterval === 'month'
      ? dataSecurityBundle.monthly_unit_amount
      : Math.round((dataSecurityBundle?.yearly_unit_amount || 0) / 12);
  const formattedPrice = (price / 100).toFixed(0);

  // Service modal handlers
  const handleBAASubmit = useCallback(
    async (data: BAAFormData) => {
      try {
        await submitBAA({
          baaRequest: {
            name: data.name,
            email: data.email,
            entity_name: data.entityName,
          },
        }).unwrap();
        setShowBAAModal(false);
      } catch (error) {
        console.error('Failed to submit BAA:', error);
        enqueueSnackbar('Failed to submit BAA. Please try again.', { variant: 'error' });
      }
    },
    [submitBAA],
  );

  const handleCOISubmit = useCallback(
    async (data: COIFormData) => {
      try {
        await submitCOI({
          coiRequest: {
            business_name: data.businessName,
            address: {
              line1: data.addressLine1,
              line2: data.addressLine2 || null,
              city: data.city,
              state: data.state,
              zip: data.zip,
              country: data.country,
            },
          },
        }).unwrap();
        setShowCOIModal(false);
      } catch (error) {
        console.error('Failed to submit COI:', error);
        enqueueSnackbar('Failed to submit COI. Please try again.', { variant: 'error' });
      }
    },
    [submitCOI],
  );

  const handleRetentionSubmit = useCallback(
    async (data: DataRetentionFormData) => {
      try {
        await submitDataRetention({
          dataRetentionRequest: {
            unsigned_notes_retention_days: data.unsignedNotesRetentionDays,
          },
        }).unwrap();
        setShowRetentionModal(false);
      } catch (error) {
        console.error('Failed to submit data retention settings:', error);
        enqueueSnackbar('Failed to update data retention settings. Please try again.', {
          variant: 'error',
        });
      }
    },
    [submitDataRetention],
  );

  return (
    <>
      <SecurityOverviewSection />

      {isLoading ? (
        <CenteredSpinner sx={{ flexGrow: 1 }} />
      ) : hasDataSecurityBundle ? (
        // User has the bundle - show three service cards
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <DataSecurityServiceCard
              title="Business Associate Agreement (BAA)"
              description="A HIPAA-aligned BAA built for behavioral-health workflows—clear, balanced, and designed to reduce legal friction."
              status={serviceStatuses.baa}
              onSetup={() => setShowBAAModal(true)}
              setupButtonText="Setup"
            />

            <DataSecurityServiceCard
              title="Certificate of Insurance (COI)"
              description="Get a Certificate of Insurance naming your organization as certificate holder, plus supporting policy documentation for procurement."
              status={serviceStatuses.coi}
              onSetup={() => setShowCOIModal(true)}
              setupButtonText="Setup"
            />

            <DataSecurityServiceCard
              title="Data Retention Policy"
              description="Set retention rules based on how you use JotPsych, with automatic deletion for unsigned notes after your chosen period."
              status={serviceStatuses.dataRetention}
              onSetup={() => setShowRetentionModal(true)}
              setupButtonText="Setup"
            />
          </Box>

          {/* Setup Modals */}
          <BAASetupModal
            open={showBAAModal}
            onClose={() => setShowBAAModal(false)}
            onSubmit={handleBAASubmit}
          />

          <COISetupModal
            open={showCOIModal}
            onClose={() => setShowCOIModal(false)}
            onSubmit={handleCOISubmit}
          />

          <DataRetentionSetupModal
            open={showRetentionModal}
            onClose={() => setShowRetentionModal(false)}
            onSubmit={handleRetentionSubmit}
          />
        </>
      ) : dataSecurityBundle ? (
        // User doesn't have the bundle - show purchase option
        <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
          <CardContent>
            {/* Header with Icon and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Security sx={{ fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {dataSecurityBundle.name}
              </Typography>
            </Box>

            {dataSecurityBundle.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {dataSecurityBundle.description}
              </Typography>
            )}

            {/* Billing Interval Toggle */}
            <Box sx={{ mb: 2 }}>
              <BillingIntervalToggle
                value={effectiveBillingInterval}
                onChange={setBillingInterval}
                discountRange={annualDiscountRange}
              />
              {isPreviewingOtherInterval && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  You&apos;ll be billed at your current cycle (
                  {orgBillingInterval === 'year' ? 'Annual' : 'Monthly'}).
                </Typography>
              )}
            </Box>

            {/* Pricing */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                ${formattedPrice}
                <Typography component="span" variant="body1" sx={{ fontWeight: 400, ml: 0.5 }}>
                  /mo
                </Typography>
              </Typography>
              {effectiveBillingInterval === 'year' && (
                <Typography variant="caption" color="text.secondary">
                  Billed annually
                </Typography>
              )}
              {organizationSize && organizationSize > 1 ? (
                <Typography variant="body2" color="text.secondary">
                  Per user • {organizationSize} users in your organization
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Per user
                </Typography>
              )}
            </Box>

            {/* Features */}
            {dataSecurityBundle.marketing_features &&
              dataSecurityBundle.marketing_features.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    What&apos;s included:
                  </Typography>
                  {dataSecurityBundle.marketing_features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <CheckCircle
                        sx={{
                          color: 'success.main',
                          fontSize: '1.2rem',
                          mt: 0.25,
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {feature.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

            {/* Add to Plan Button and Learn More Link */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleAddToPlan}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 4,
                }}
              >
                Add to Plan
              </Button>
              <Link
                href={learnMoreUrl}
                target={isInternalUrl(learnMoreUrl) ? undefined : '_blank'}
                rel={isInternalUrl(learnMoreUrl) ? undefined : 'noopener noreferrer'}
                underline="hover"
                onClick={handleLearnMoreClick}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.875rem',
                  color: 'primary.main',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Learn more
                {!isInternalUrl(learnMoreUrl) && <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
              </Link>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info">
          Data & Security bundle is not currently available. Please check back later.
        </Alert>
      )}

      {/* Purchase Confirmation Dialog */}
      {dataSecurityBundle && (
        <DataSecurityPurchaseDialog
          open={showPurchaseDialog}
          bundle={dataSecurityBundle}
          billingInterval={effectiveBillingInterval}
          organizationSize={organizationSize}
          loading={isAddingBundle}
          onClose={() => {
            setShowPurchaseDialog(false);
            setPurchaseError(null);
          }}
          onConfirm={handleConfirmPurchase}
        />
      )}

      {/* Error Display */}
      {purchaseError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setPurchaseError(null)}>
          {purchaseError}
        </Alert>
      )}
    </>
  );
}
